import React from 'react'
import { Flex } from 'rebass'
import styled from 'styled-components'
import { TYPE } from '../../Theme'
import { formattedNum, getPoolLink } from '../../utils'
import { formatAmount } from '../../utils/formatAmount'
import { AutoColumn } from '../Column'
import DoubleTokenLogo from '../DoubleLogo'
import Link, { BasicLink } from '../Link'

export const Card = styled.div`
  background: ${({ theme }) => theme.bg2};
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: transform 0.2s ease;
  border: none;
  min-height: 280px;

  :hover {
    transform: translateY(-2px);
  }
`

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.bg3};
  margin: 16px 0;
`

export const TokenPairHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

export const TokenLogosContainer = styled.div`
  display: grid;
  grid-template-columns: max-content;
  grid-template-rows: max-content;
  place-items: start;
  position: relative;
`

export const VolumeSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

export const PriceChangeText = styled(TYPE.primary)<{ isPositive: boolean; isNegative: boolean; isZero: boolean }>`
  color: ${({ isPositive, isNegative, isZero, theme }) =>
    isPositive || isZero ? theme.green1 : isNegative ? theme.red1 : theme.text1};
  text-align: right;
`

export const StatsSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const StatRow = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.bg3};
  border-bottom: 1px solid ${({ theme }) => theme.bg3};

  &:not(:first-child) {
    border-top: none;
  }
`

export const LiquidityButton = styled.button`
  background: white;
  color: ${({ theme }) => theme.bg1};
  border: none;
  border-radius: 50px;
  padding: 12px 32px;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;

  :hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`

export interface CryptoCardProps {
  item: any
  allPairs: any
}

export default function CryptoCard({ allPairs, item }: CryptoCardProps): JSX.Element {
  const pairData = allPairs[item?.id || '']

  if (!pairData) return null

  const priceChange = +pairData.liquidityChangeUSD.toFixed(2)
  const isPositive = priceChange > 0
  const isNegative = priceChange < 0
  const isZero = priceChange === 0

  return (
    <Card>
      {/* Token Pair Header - Clickable to navigate to pool detail */}
      <Flex
        alignItems="center"
        marginBottom="20px"
        as={BasicLink}
        to={`/pools/${item?.id}`}
        style={{ cursor: 'pointer' }}
      >
        <TokenPairHeader>
          <TokenLogosContainer>
            <DoubleTokenLogo size={48} a0={item.token0.id} a1={item.token1.id} />
          </TokenLogosContainer>
          <TYPE.primary style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, marginLeft: '12px' }}>
            {pairData.token0.symbol + '/' + pairData.token1.symbol}
          </TYPE.primary>
        </TokenPairHeader>
      </Flex>

      {/* Volume and Price Change Section - Also clickable */}
      <AutoColumn as={BasicLink} to={`/pools/${item?.id}`} style={{ cursor: 'pointer' }}>
        <VolumeSection>
          <AutoColumn style={{ alignItems: 'flex-start' }}>
            <TYPE.light style={{ fontSize: '14px', lineHeight: '1.4' }}>Volume</TYPE.light>
            <TYPE.primary style={{ fontSize: '18px', fontWeight: '600', lineHeight: '1.4' }}>
              ${formatAmount(+Number(pairData.volumeUSD).toFixed(1))}
            </TYPE.primary>
          </AutoColumn>

          <AutoColumn style={{ alignItems: 'flex-end' }}>
            <TYPE.light style={{ fontSize: '14px', lineHeight: '1.4' }}>Price Change</TYPE.light>
            <PriceChangeText
              isPositive={isPositive}
              isNegative={isNegative}
              isZero={isZero}
              style={{ fontSize: '18px', fontWeight: '600', lineHeight: 'normal' }}
            >
              {isPositive ? '+' : ''}
              {formatAmount(priceChange, 2, 2)}%
            </PriceChangeText>
          </AutoColumn>
        </VolumeSection>
      </AutoColumn>

      {/* Statistics Section - Also clickable */}
      <AutoColumn as={BasicLink} to={`/pools/${item?.id}`} style={{ cursor: 'pointer' }}>
        <StatsSection>
          <StatRow>
            <TYPE.light style={{ fontSize: '14px', lineHeight: '1.4' }}>24h Volume</TYPE.light>
            <TYPE.light style={{ fontSize: '14px', color: 'white', lineHeight: '1.4' }}>
              {formattedNum(pairData.oneDayVolumeUSD?.toFixed(0) || pairData.volumeUSD, true)}
            </TYPE.light>
          </StatRow>

          <StatRow>
            <TYPE.light style={{ fontSize: '14px', lineHeight: '1.4' }}>TVL</TYPE.light>
            <TYPE.light style={{ fontSize: '14px', color: 'white', lineHeight: '1.4' }}>
              {formattedNum(pairData.reserveUSD || pairData.trackedReserveUSD, true)}
            </TYPE.light>
          </StatRow>
        </StatsSection>
      </AutoColumn>

      {/* Provide Liquidity Button - Remains as external link */}
      <Link external href={getPoolLink(item?.token0?.id, item?.token1?.id)} style={{ width: '100%' }}>
        <LiquidityButton>Provide Liquidity</LiquidityButton>
      </Link>
    </Card>
  )
}
